import { useSnackbarStack } from '@superdispatch/ui';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

import { ONE_MINUTE } from 'shared/constants/NumberConstants';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { useAppFormik } from 'shared/form/AppFormik';
import {
  FormikDrawerActions,
  FormikDrawerContent,
} from 'shared/form/FormikDrawer';
import {
  SendVerificationCodeMethod,
  verificationCodeSchema,
  VerificationCodeForm,
} from 'shared/form/VerificationCodeForm';
import {
  useCarrierProfileAPI,
  useSendACHPaymentInfoVerificationCode,
} from 'shared/modules/carrier-profile/data/CarrierProfileAPI';
import { ACHPaymentInfoDTO } from 'shared/modules/carrier-profile/data/CarrierProfileDTO';

const CODE_SENT_MESSAGES: Record<SendVerificationCodeMethod, string> = {
  email: 'Verification code sent',
  sms: 'Verification code sent via text message',
};

function getResendAvailableAt() {
  return DateTime.local().plus({ milliseconds: ONE_MINUTE });
}

interface VerificationFormValues {
  code: string;
}

interface ACHChangeBankDetailsConfirmFormProps {
  updatedPaymentInfo: ACHPaymentInfoDTO;
  onComplete: () => void;
}

function useACHChangeBankDetailsConfirmForm({
  updatedPaymentInfo,
  onComplete,
}: ACHChangeBankDetailsConfirmFormProps) {
  const { addSnackbar } = useSnackbarStack();
  const [resendAvailableAt, setResendAvailableAt] = useState<
    DateTime | undefined
  >(getResendAvailableAt);
  const [verificationMethod, setVerificationMethod] =
    useState<SendVerificationCodeMethod>('email');

  const { mutateAsync, data: recipientData } =
    useSendACHPaymentInfoVerificationCode();
  const { updateACHPaymentInfo } = useCarrierProfileAPI();

  const formik = useAppFormik<VerificationFormValues>({
    validationSchema: verificationCodeSchema,
    initialValues: { code: '' },
    onSubmit: ({ code }) =>
      updateACHPaymentInfo({
        ...updatedPaymentInfo,
        verification_code: code,
        verification_method: verificationMethod,
      }),
    onSubmitSuccess: () => {
      onComplete();
    },
    getErrorMessage: (error) =>
      `Failed to update ACH payment info: ${error.message}`,
  });

  async function sendVerificationCode(method: SendVerificationCodeMethod) {
    try {
      await mutateAsync(method);
      setVerificationMethod(method);
      setResendAvailableAt(getResendAvailableAt());
      formik.resetForm();
      addSnackbar(CODE_SENT_MESSAGES[method], { variant: 'success' });
    } catch (error) {
      addSnackbar((error as Error).message, { variant: 'error' });
    }
  }

  return {
    formik,
    resendAvailableAt,
    verificationMethod,
    recipient: recipientData?.data.recipient,
    sendCode: () => {
      void sendVerificationCode(verificationMethod);
    },
    onSendEmail: () => {
      void sendVerificationCode('email');
    },
    onSendTextMessage: () => {
      void sendVerificationCode('sms');
    },
  };
}

interface ACHChangeBankDetailsConfirmStepProps {
  updatedPaymentInfo: ACHPaymentInfoDTO;
  onClose: () => void;
  onComplete: () => void;
}

export function ACHChangeBankDetailsConfirmStep({
  updatedPaymentInfo,
  onClose,
  onComplete,
}: ACHChangeBankDetailsConfirmStepProps) {
  const {
    formik,
    sendCode,
    resendAvailableAt,
    verificationMethod,
    recipient,
    onSendEmail,
    onSendTextMessage,
  } = useACHChangeBankDetailsConfirmForm({ updatedPaymentInfo, onComplete });

  useEffect(() => {
    sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormikDrawerContent
      formik={formik}
      title="Confirm It's You"
      onClose={onClose}
      disableAutoClose={true}
      actions={
        isWebView && (
          <FormikDrawerActions
            submitButtonLabel="Continue"
            submitDisabled={!formik.dirty || !formik.isValid}
            fullWidth={true}
          />
        )
      }
    >
      <VerificationCodeForm
        recipient={recipient}
        verificationMethod={verificationMethod}
        resendAvailableAt={resendAvailableAt}
        onResend={sendCode}
        onSendEmail={onSendEmail}
        onSendTextMessage={onSendTextMessage}
        hideSubmitButton={isWebView}
      />
    </FormikDrawerContent>
  );
}
